/**
 * Shared JS API for Furnvoy Shopify Theme
 */

document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initMegaMenu();
  initMobileDrawer();
  initCartDrawer();
  initAjaxCart();
  initHeroSliders();
  initRails();
  initReels();
  initVariantPickers();
  initQtySteppers();
  initAccordions();
});

/**
 * Header transparency logic
 */
function initHeader() {
  const header = document.querySelector('[data-header]');
  if (!header || !header.dataset.transparentTop) return;

  const logo = header.querySelector('img[data-logo-dark][data-logo-light]');

  const handleScroll = () => {
    if (window.scrollY > 40) {
      header.classList.remove('bg-transparent', 'text-background');
      header.classList.add('bg-background', 'text-ink', 'shadow-[0_1px_0_0_var(--border)]');
      if (logo) logo.src = logo.dataset.logoDark;
    } else {
      header.classList.add('bg-transparent', 'text-background');
      header.classList.remove('bg-background', 'text-ink', 'shadow-[0_1px_0_0_var(--border)]');
      if (logo) logo.src = logo.dataset.logoLight;
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll();
}

/**
 * Mega Menu logic
 */
function initMegaMenu() {
  const header = document.querySelector('[data-header]');
  if (!header) return;

  const triggers = header.querySelectorAll('[data-menu-trigger]');
  const panels = header.querySelectorAll('[data-menu-panel]');

  if (triggers.length === 0 || panels.length === 0) return;

  const openPanel = (title) => {
    panels.forEach(p => {
      p.classList.remove('max-h-[500px]', 'opacity-100', 'pointer-events-auto');
      p.classList.add('max-h-0', 'opacity-0', 'pointer-events-none');
    });
    const panel = Array.from(panels).find(p => p.dataset.menuPanel === title);
    if (panel) {
      panel.classList.remove('max-h-0', 'opacity-0', 'pointer-events-none');
      panel.classList.add('max-h-[500px]', 'opacity-100', 'pointer-events-auto');
    }
  };

  const closePanels = () => {
    panels.forEach(p => {
      p.classList.remove('max-h-[500px]', 'opacity-100', 'pointer-events-auto');
      p.classList.add('max-h-0', 'opacity-0', 'pointer-events-none');
    });
  };

  triggers.forEach(trigger => {
    trigger.addEventListener('mouseenter', () => openPanel(trigger.dataset.menuTrigger));
    trigger.addEventListener('focus', () => openPanel(trigger.dataset.menuTrigger));
  });

  panels.forEach(panel => {
    panel.addEventListener('mouseenter', () => openPanel(panel.dataset.menuPanel));
  });

  header.addEventListener('mouseleave', closePanels);
}

/**
 * Mobile Drawer logic (side menu)
 */
function initMobileDrawer() {
  const drawer = document.querySelector('[data-drawer]');
  if (!drawer) return;

  const openButtons = document.querySelectorAll('[data-drawer-open]');
  const closeButtons = document.querySelectorAll('[data-drawer-close]');
  const groups = drawer.querySelectorAll('[data-drawer-panel]');
  const backs = drawer.querySelectorAll('[data-drawer-back]');
  const triggers = drawer.querySelectorAll('[data-drawer-group]');

  const openDrawer = () => {
    drawer.classList.remove('-translate-x-full');
    drawer.classList.add('translate-x-0');
    document.body.classList.add('overflow-hidden');
  };

  const closeDrawer = () => {
    drawer.classList.remove('translate-x-0');
    drawer.classList.add('-translate-x-full');
    document.body.classList.remove('overflow-hidden');
    // Reset groups
    groups.forEach(g => {
      g.classList.remove('translate-x-0');
      g.classList.add('translate-x-full');
    });
  };

  openButtons.forEach(btn => btn.addEventListener('click', openDrawer));
  closeButtons.forEach(btn => btn.addEventListener('click', closeDrawer));

  triggers.forEach(trigger => {
    trigger.addEventListener('click', () => {
      const target = trigger.dataset.drawerGroup;
      const group = Array.from(groups).find(g => g.dataset.drawerPanel === target);
      if (group) {
        group.classList.remove('translate-x-full');
        group.classList.add('translate-x-0');
      }
    });
  });

  backs.forEach(back => {
    back.addEventListener('click', () => {
      const panel = back.closest('[data-drawer-panel]');
      if (panel) {
        panel.classList.remove('translate-x-0');
        panel.classList.add('translate-x-full');
      }
    });
  });
}

/**
 * Cart Drawer logic
 */
let cartDrawerOpen = false;

function cartDrawerEl() {
  return document.querySelector('[data-cart-drawer]');
}

function cartOverlayEl() {
  return document.querySelector('[data-cart-overlay]');
}

function applyCartDrawerState() {
  const drawer = cartDrawerEl();
  const overlay = cartOverlayEl();
  if (!drawer) return;

  if (cartDrawerOpen) {
    drawer.classList.remove('translate-x-full');
    drawer.classList.add('translate-x-0');
    if (overlay) {
      overlay.classList.remove('opacity-0', 'pointer-events-none');
      overlay.classList.add('opacity-100', 'pointer-events-auto');
    }
    document.body.classList.add('overflow-hidden');
  } else {
    drawer.classList.remove('translate-x-0');
    drawer.classList.add('translate-x-full');
    if (overlay) {
      overlay.classList.add('opacity-0', 'pointer-events-none');
      overlay.classList.remove('opacity-100', 'pointer-events-auto');
    }
    document.body.classList.remove('overflow-hidden');
  }
}

function openCartDrawer() {
  cartDrawerOpen = true;
  applyCartDrawerState();
}

function closeCartDrawer() {
  cartDrawerOpen = false;
  applyCartDrawerState();
}

function initCartDrawer() {
  window.openCartDrawer = openCartDrawer;
  window.closeCartDrawer = closeCartDrawer;

  // Fully delegated so it survives drawer re-renders
  document.addEventListener('click', async (e) => {
    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      openCartDrawer();
      return;
    }

    if (e.target.closest('[data-cart-close]') || e.target.closest('[data-cart-overlay]')) {
      closeCartDrawer();
      return;
    }

    const minus = e.target.closest('[data-qty-minus]');
    const plus = e.target.closest('[data-qty-plus]');
    const remove = e.target.closest('[data-cart-remove]');
    if (!minus && !plus && !remove) return;

    const lineItem = e.target.closest('[data-cart-line]');
    if (!lineItem) return;

    e.preventDefault();

    const key = lineItem.dataset.cartLine;
    const input = lineItem.querySelector('[data-qty-input]');
    let quantity = parseInt(input?.value || '0', 10) || 0;

    if (minus) quantity = Math.max(0, quantity - 1);
    if (plus) quantity += 1;
    if (remove) quantity = 0;

    lineItem.style.opacity = '0.5';
    lineItem.style.pointerEvents = 'none';
    await updateCart(key, quantity);
  });
}

async function fetchCartDrawerHtml() {
  const res = await fetch(`${window.location.pathname}?section_id=cart-drawer`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' }
  });
  if (!res.ok) return null;
  return await res.text();
}

async function refreshCartDrawer() {
  const html = await fetchCartDrawerHtml();
  const root = document.getElementById('cart-drawer-root');
  if (!html || !root) return;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  // section_id response wraps content in #shopify-section-cart-drawer
  const section = doc.querySelector('#shopify-section-cart-drawer') || doc.body;
  root.innerHTML = section.innerHTML;

  applyCartDrawerState();
  initQtySteppers();
}

async function refreshCartCount() {
  try {
    const res = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!res.ok) return;
    const cart = await res.json();
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.classList.toggle('hidden', cart.item_count === 0);
    });
  } catch (err) {
    /* noop */
  }
}

async function updateCart(key, quantity) {
  try {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
  } catch (err) {
    console.error('Error updating cart:', err);
  }
  await Promise.all([refreshCartDrawer(), refreshCartCount(), refreshMainCart()]);
}

async function refreshMainCart() {
  const cartContainer = document.querySelector('#shopify-section-main-cart');
  if (!cartContainer) return;
  try {
    const res = await fetch(`${window.location.pathname}?section_id=main-cart`);
    if (!res.ok) return;
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = doc.querySelector('#shopify-section-main-cart');
    if (section) cartContainer.innerHTML = section.innerHTML;
  } catch (err) {
    /* noop */
  }
}

/**
 * Ajax Add to Cart
 */
function initAjaxCart() {
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-cart-add]');
    if (!form) return;

    e.preventDefault();
    const submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      });

      if (!res.ok) {
        console.error('Add to cart error:', await res.text());
        return;
      }

      openCartDrawer();
      await Promise.all([refreshCartDrawer(), refreshCartCount()]);
    } catch (err) {
      console.error('Error adding to cart:', err);
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  });
}


/**
 * Hero Slider logic
 */
function initHeroSliders() {
  const sliders = document.querySelectorAll('[data-hero]');

  sliders.forEach(slider => {
    const count = parseInt(slider.dataset.heroCount || 0);
    const dots = slider.querySelectorAll('[data-hero-dot]');
    const slides = slider.querySelectorAll('[data-hero-slide]');
    let currentIndex = 0;
    let timer = null;

    if (count <= 1 || slides.length === 0) return;

    const goToSlide = (index) => {
      currentIndex = (index + count) % count;

      slides.forEach((slide, i) => {
        const img = slide.querySelector('img');
        if (i === currentIndex) {
          slide.classList.remove('opacity-0');
          slide.classList.add('opacity-100');
          slide.setAttribute('aria-hidden', 'false');
          if (img) img.classList.add('scale-105');
        } else {
          slide.classList.add('opacity-0');
          slide.classList.remove('opacity-100');
          slide.setAttribute('aria-hidden', 'true');
          if (img) img.classList.remove('scale-105');
        }
      });

      dots.forEach((dot, i) => {
        if (i === currentIndex) {
          dot.classList.remove('w-5', 'bg-white/45');
          dot.classList.add('w-10', 'bg-white');
        } else {
          dot.classList.add('w-5', 'bg-white/45');
          dot.classList.remove('w-10', 'bg-white');
        }
      });

      // Swap hero text content
      const contents = slider.querySelectorAll('[data-hero-content]');
      contents.forEach((content, i) => {
        content.classList.toggle('hidden', i !== currentIndex);
        content.classList.toggle('flex', i === currentIndex);
      });
    };

    const startTimer = () => {
      timer = setInterval(() => goToSlide(currentIndex + 1), 6500);
    };

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        clearInterval(timer);
        goToSlide(i);
        startTimer();
      });
    });

    startTimer();
  });
}

/**
 * Rail logic (Horizontal Scroll)
 */
function initRails() {
  const rails = document.querySelectorAll('[data-rail]');

  rails.forEach(rail => {
    const prev = rail.querySelector('[data-rail-prev]');
    const next = rail.querySelector('[data-rail-next]');

    if (prev && next) {
      prev.addEventListener('click', () => {
        rail.scrollBy({ left: -300, behavior: 'smooth' });
      });
      next.addEventListener('click', () => {
        rail.scrollBy({ left: 300, behavior: 'smooth' });
      });
    }
  });
}

/**
 * Reels Carousel logic
 * Autoplays every video inside the section (product videos + Instagram app videos)
 * when it scrolls into view, and pauses when it leaves.
 */
function initReels() {
  const containers = document.querySelectorAll('[data-reels]');
  if (containers.length === 0) return;

  let globalMuted = true;
  let audibleInlineVideo = null;

  // Instafeed delegates some popup handlers above the generated rail and
  // injects its modal beside the app block. Block reel-tile activation at the
  // document level and remove any modal that an already-registered app handler
  // manages to inject. The dedicated sound control is the only allowed click.
  const isReelActivation = (target) => {
    if (!(target instanceof Element)) return false;
    if (target.closest('.furnvoy-instafeed-sound')) return false;
    return Boolean(target.closest('[data-reels-app], .furnvoy-instafeed-slide'));
  };
  ['pointerup', 'mouseup', 'click', 'touchend'].forEach(type => {
    document.addEventListener(type, event => {
      if (!isReelActivation(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    }, true);
  });

  const removeInstafeedPopup = () => {
    document.querySelectorAll('.instafeed-modals-wrapper, .lightbox-instagram').forEach(node => node.remove());
    document.documentElement.classList.remove('instafeed-modal-open');
    document.body.classList.remove('instafeed-modal-open');
    document.body.style.removeProperty('overflow');
  };
  const popupObserver = new MutationObserver(removeInstafeedPopup);
  popupObserver.observe(document.body, { childList: true, subtree: true });
  removeInstafeedPopup();

  const prepareInstafeedRail = () => {
    containers.forEach(container => {
      const app = container.querySelector('[data-reels-app]');
      if (!app) return;

      // Instafeed marks the whole rail as touch-scroll blocking. Removing that
      // attribute lets vertical page swipes pass through the reels on mobile.
      app.querySelectorAll('[data-instafeed-prevent-touch-scroll]').forEach(node => {
        node.removeAttribute('data-instafeed-prevent-touch-scroll');
      });

      // The app renders different markup depending on its layout setting, so
      // instead of relying on its class names we detect the real rail: the
      // deepest element whose direct children each hold one post media node.
      const matches = [];
      app.querySelectorAll('*').forEach(node => {
        const kids = Array.from(node.children);
        if (kids.length < 2) return;
        const withMedia = kids.filter(k => k.querySelector('img, video, iframe'));
        if (withMedia.length < 2 || withMedia.length < kids.length - 1) return;
        matches.push(node);
      });
      // Deepest match = the actual slide track (outer wrappers also match).
      const rail = matches.filter(node => !matches.some(other => other !== node && node.contains(other)))[0];
      if (!rail) return;


      rail.classList.add('furnvoy-instafeed-rail');
      // Its slider repeatedly writes transforms/sizes inline — clear them.
      rail.style.setProperty('transform', 'none', 'important');
      rail.style.setProperty('transition', 'none', 'important');
      Array.from(rail.children).forEach((slide, i) => {
        slide.classList.add('furnvoy-instafeed-slide');
        slide.style.removeProperty('width');
        slide.style.removeProperty('height');
        slide.style.removeProperty('margin');
        if (i >= 4) slide.classList.add('furnvoy-instafeed-slide--hidden');
      });

      // The reels play inline, so the app's lightbox popup is not needed:
      // swallow tile clicks (the sound button keeps working).
      if (!rail.dataset.furnvoyNoPopup) {
        rail.dataset.furnvoyNoPopup = 'true';
        ['pointerdown', 'mousedown', 'mouseup', 'click', 'touchend'].forEach(type => {
          rail.addEventListener(
            type,
            (event) => {
              if (event.target instanceof Element && event.target.closest('.furnvoy-instafeed-sound')) return;
              event.preventDefault();
              event.stopPropagation();
              if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
            },
            true
          );
        });
      }
    });
  };

  /**
   * Instafeed keeps the original video URLs in its feed response even when it
   * renders poster images in the grid. Read that response directly instead of
   * synthetically clicking the tiles/lightbox; this preserves a stable
   * one-post-to-one-video mapping and leaves the app's click behaviour intact.
   */
  const mountInlineVideo = (slide, src, postId) => {
    if (!src || slide.dataset.furnvoyInline === 'done') return;
    slide.dataset.furnvoyInline = 'done';
    if (postId) slide.dataset.furnvoyPostId = String(postId);
    const video = document.createElement('video');
    video.className = 'furnvoy-instafeed-video';
    video.src = src;
    video.loop = true;
    video.muted = true;
    video.defaultMuted = true;
    video.autoplay = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('preload', 'metadata');
    const host = slide.querySelector('a, div') || slide;
    host.appendChild(video);
    video.load();
    const start = () => {
      const promise = video.play();
      if (promise) promise.catch(() => {});
    };
    video.addEventListener('loadedmetadata', start, { once: true });
    video.addEventListener('canplay', start, { once: true });
    start();

    // Per-tile sound toggle. Only one reel may play audio at a time: turning
    // one on mutes every other inline reel video on the page.
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'furnvoy-instafeed-sound';
    button.setAttribute('aria-label', 'Play sound for this reel');
    const paint = () => {
      button.dataset.state = video.muted ? 'muted' : 'unmuted';
      button.innerHTML = video.muted
        ? '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M11 5 6.5 9H3v6h3.5L11 19V5z"/><path d="M16 9.5l4 5M20 9.5l-4 5" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round"/></svg>'
        : '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M11 5 6.5 9H3v6h3.5L11 19V5z"/><path d="M14.5 8.5a4.5 4.5 0 0 1 0 7M17 6a8 8 0 0 1 0 12" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round"/></svg>';
      button.setAttribute('aria-label', video.muted ? 'Play sound for this reel' : 'Mute this reel');
    };
    const toggle = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const turnOn = video.muted;
      document.querySelectorAll('.furnvoy-instafeed-video').forEach(other => {
        if (other === video) return;
        other.muted = true;
        const otherButton = other.parentElement && other.parentElement.querySelector('.furnvoy-instafeed-sound');
        if (otherButton) {
          otherButton.dataset.state = 'muted';
          otherButton.dispatchEvent(new CustomEvent('furnvoy:repaint'));
        }
      });
      video.muted = !turnOn;
      if (turnOn) {
        audibleInlineVideo = video;
        video.volume = 1;
        const promise = video.play();
        if (promise) promise.catch(() => {});
      } else if (audibleInlineVideo === video) {
        audibleInlineVideo = null;
      }
      paint();
    };
    button.addEventListener('furnvoy:repaint', paint);
    button.addEventListener('click', toggle);
    button.addEventListener('pointerdown', (e) => e.stopPropagation());
    paint();
    host.appendChild(button);
    scan();
  };


  // Feed responses are small and change rarely: cache them for the session so
  // repeat page loads render the reels immediately instead of waiting on the
  // network round trip again.
  const FEED_TTL = 10 * 60 * 1000;
  const readCachedFeed = (url) => {
    try {
      const raw = sessionStorage.getItem('furnvoy:feed:' + url);
      if (!raw) return null;
      const entry = JSON.parse(raw);
      if (!entry || Date.now() - entry.t > FEED_TTL) return null;
      return Array.isArray(entry.d) ? entry.d : null;
    } catch (_) {
      return null;
    }
  };
  const writeCachedFeed = (url, data) => {
    try {
      sessionStorage.setItem('furnvoy:feed:' + url, JSON.stringify({ t: Date.now(), d: data }));
    } catch (_) {
      // Storage may be full or blocked — caching is an optimisation only.
    }
  };

  let harvesting = false;
  const harvestInstafeedVideos = async () => {
    if (harvesting) return;
    harvesting = true;
    try {
      // Some mobile Instafeed configurations leave #insta-feed empty because
      // their own layout is limited to two posts. Only then build our own rail —
      // otherwise the app rail plus a generated rail would show two rows.
      document.querySelectorAll('[data-reels-app]').forEach(app => {
        const generated = app.querySelector('[data-furnvoy-generated="true"]');
        const nativeRail = Array.from(app.querySelectorAll('.furnvoy-instafeed-rail')).find(
          node => node.dataset.furnvoyGenerated !== 'true'
        );
        // Only count media the app rendered itself — media inside our own
        // generated rail must never trigger its removal (flicker loop).
        const hasAppMedia = Array.from(app.querySelectorAll('img, video, iframe')).some(
          node => !node.closest('[data-furnvoy-generated="true"]')
        );

        // The app rendered its own tiles: drop any rail we created earlier.
        if (nativeRail || hasAppMedia) {
          if (generated) generated.remove();
          return;
        }
        if (generated) return;
        const rail = document.createElement('div');
        rail.className = 'furnvoy-instafeed-rail';
        rail.dataset.furnvoyGenerated = 'true';
        app.appendChild(rail);
      });
      const rails = Array.from(document.querySelectorAll('.furnvoy-instafeed-rail')).filter(
        rail =>
          rail.dataset.furnvoyGenerated !== 'true' ||
          !Array.from(
            rail.closest('[data-reels-app]')?.querySelectorAll('.furnvoy-instafeed-rail') || []
          ).some(other => other !== rail && other.dataset.furnvoyGenerated !== 'true')
      );

      for (const rail of rails) {
        // The feed id can sit on the rail or on any ancestor wrapper, and some
        // layouts omit it entirely — then fall back to the only live instance.
        const instances = window.InstafeedAPI?.settings?.instances || {};
        const holder = rail.closest('[data-feed-id]');
        const feedId = rail.getAttribute('data-feed-id') || holder?.getAttribute('data-feed-id');
        const exactInstance = feedId ? instances[feedId] : null;
        const candidates = [exactInstance, ...Object.values(instances)].filter(
          (instance, index, list) =>
            instance &&
            list.indexOf(instance) === index &&
            typeof instance.T === 'function'
        );

        // Mobile Instafeed layouts can omit data-feed-id while more than one
        // app instance exists on the page. Try each live instance and retain
        // the first response that contains enough recent posts for this rail.
        let instance = null;
        let posts = [];
        for (const candidate of candidates) {
          try {
            const feedUrl = new URL(candidate.T(), window.location.href);
            feedUrl.searchParams.set('limit', '4');
            const key = feedUrl.toString();
            let candidatePosts = readCachedFeed(key);
            if (!candidatePosts) {
              const response = await fetch(key);
              if (!response.ok) continue;
              const payload = await response.json();
              candidatePosts = Array.isArray(payload?.data) ? payload.data : [];
              if (candidatePosts.length) writeCachedFeed(key, candidatePosts);
            }
            if (candidatePosts.length > posts.length) {
              instance = candidate;
              posts = candidatePosts;
            }
            if (candidatePosts.length >= 4) break;
          } catch (_) {
            // A stale app instance must not prevent the remaining feeds from
            // being checked.
          }
        }
        if (!instance || posts.length === 0) continue;

        // Generated rails have no Instafeed slide to clone. Build five clean
        // media hosts directly from the response instead.
        if (rail.dataset.furnvoyGenerated === 'true') {
          rail.replaceChildren();
          posts.slice(0, 4).forEach(post => {
            const slide = document.createElement('div');
            slide.className = 'furnvoy-instafeed-slide';
            const host = document.createElement('div');
            const image = document.createElement('img');
            image.alt = post.caption?.text ? post.caption.text.slice(0, 120) : 'Furnvoy Instagram reel';
            image.loading = 'eager';
            image.decoding = 'async';
            image.setAttribute('fetchpriority', 'high');
            image.src = post.images?.standard_resolution?.url || post.thumbnail_url || '';
            host.appendChild(image);
            slide.appendChild(host);
            rail.appendChild(slide);
          });
        }

        // The app can render fewer tiles than posts (e.g. its mobile layout
        // only outputs two). Clone the tile markup so the newest five reels
        // are always present in the swipe rail.
        const wanted = Math.min(4, posts.length);
        const template = rail.children[0];
        while (template && rail.children.length < wanted) {
          const clone = template.cloneNode(true);
          clone.removeAttribute('data-furnvoy-inline');
          clone.removeAttribute('data-furnvoy-post-id');
          delete clone.dataset.furnvoyInline;
          delete clone.dataset.furnvoyPostId;
          clone.querySelectorAll('.furnvoy-instafeed-video, .furnvoy-instafeed-sound').forEach(n => n.remove());
          clone.querySelectorAll('video').forEach(n => n.remove());
          clone.classList.add('furnvoy-instafeed-slide');
          clone.classList.remove('furnvoy-instafeed-slide--hidden');
          rail.appendChild(clone);
        }

        // Never show more than one row of five: hide any surplus app tiles.
        Array.from(rail.children).forEach((slide, i) => {
          if (i >= 4) slide.classList.add('furnvoy-instafeed-slide--hidden');
        });
        const slides = Array.from(rail.children).slice(0, 4);

        slides.forEach((slide, index) => {
          slide.classList.add('furnvoy-instafeed-slide');
          slide.classList.remove('furnvoy-instafeed-slide--hidden');
          const post = posts[index];
          if (!post) return;
          const postId = post.id || post.pk || post.code || String(index);
          if (slide.dataset.furnvoyPostId === String(postId) && slide.dataset.furnvoyInline === 'done') return;

          slide.querySelectorAll('.furnvoy-instafeed-video, .furnvoy-instafeed-sound').forEach(node => node.remove());
          delete slide.dataset.furnvoyInline;
          delete slide.dataset.furnvoyPostId;

          let src = null;
          if (typeof instance.C === 'function') {
            try {
              src = instance.C(post);
            } catch (_) {
              src = null;
            }
          }
          src = src || post.video_url || post.media_url || post.videos?.standard_resolution?.url || post.videos?.low_resolution?.url;
          if (src) mountInlineVideo(slide, src, post.id);
        });
      }
    } finally {
      harvesting = false;
    }
  };

  // Start well before the section scrolls into view so the reels are already
  // decoded and playing by the time the visitor reaches them.
  const sectionObserver = new IntersectionObserver((entries) => {
    if (entries.some(e => e.isIntersecting)) harvestInstafeedVideos();
  }, { threshold: 0, rootMargin: '1500px 0px' });
  containers.forEach(container => sectionObserver.observe(container));

  // The harvest needs a live Instafeed instance. Instead of waiting for a DOM
  // mutation or for the section to be scrolled to, poll briefly for the app to
  // register and fire immediately when it does.
  let waited = 0;
  const waitForFeed = window.setInterval(() => {
    waited += 150;
    const ready = Object.keys(window.InstafeedAPI?.settings?.instances || {}).length > 0;
    if (ready) {
      prepareInstafeedRail();
      harvestInstafeedVideos();
    }
    if (ready || waited > 12000) window.clearInterval(waitForFeed);
  }, 150);

  const appObserver = new MutationObserver(() => {
    prepareInstafeedRail();
    if (containers[0] && containers[0].getBoundingClientRect().top < window.innerHeight * 1.5) {
      harvestInstafeedVideos();
    }
  });
  containers.forEach(container => appObserver.observe(container, { childList: true, subtree: true }));
  prepareInstafeedRail();


  const isReelVideo = (video) =>
    Array.from(containers).some(c => c.contains(video));

  const prepare = (video) => {
    video.muted = video.classList.contains('furnvoy-instafeed-video')
      ? video !== audibleInlineVideo
      : globalMuted;
    video.defaultMuted = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('muted', '');
    video.setAttribute('preload', 'auto');
    if (!video.hasAttribute('loop')) video.setAttribute('loop', '');
  };

  const playVideo = (video) => {
    prepare(video);
    const promise = video.play();
    if (promise) {
      promise.catch(() => {
        // Some browsers refuse until fully muted — retry muted.
        video.muted = true;
        const retry = video.play();
        if (retry) retry.catch(() => {});
      });
    }
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        playVideo(entry.target);
      } else {
        entry.target.pause();
      }
    });
  }, { threshold: 0.15, rootMargin: '100px 0px' });

  const scan = () => {
    document.querySelectorAll('video').forEach(video => {
      if (video.dataset.reelObserved || !isReelVideo(video)) return;
      video.dataset.reelObserved = 'true';
      prepare(video);
      observer.observe(video);
      // Lightbox/popup videos are often already visible when injected.
      playVideo(video);
      video.addEventListener('loadeddata', () => playVideo(video), { once: true });
      video.addEventListener('canplay', () => playVideo(video), { once: true });
    });
  };

  scan();

  // First user interaction unlocks autoplay in stricter browsers.
  const kick = () => {
    document.querySelectorAll('video[data-reel-observed]').forEach(playVideo);
  };
  ['click', 'touchstart', 'scroll', 'keydown'].forEach(evt =>
    window.addEventListener(evt, kick, { once: true, passive: true })
  );

  containers.forEach(container => {
    container.querySelectorAll('[data-reels-mute]').forEach(btn => {
      btn.addEventListener('click', () => {
        globalMuted = !globalMuted;
        document.querySelectorAll('video[data-reel-observed]').forEach(v => {
          v.muted = globalMuted;
          if (!globalMuted) playVideo(v);
        });

        const mutedIcon = btn.querySelector('[data-mute-icon-muted]');
        const unmutedIcon = btn.querySelector('[data-mute-icon-unmuted]');
        const text = btn.querySelector('[data-mute-text]');

        if (mutedIcon) mutedIcon.classList.toggle('hidden', !globalMuted);
        if (unmutedIcon) unmutedIcon.classList.toggle('hidden', globalMuted);
        if (text) text.textContent = globalMuted ? 'Sound off' : 'Sound on';
      });
    });
  });
}


/**
 * Variant Picker logic
 */
function initVariantPickers() {
  const forms = document.querySelectorAll('[data-variant-form]');

  forms.forEach(form => {
    const options = form.querySelectorAll('[data-variant-option]');
    const select = form.querySelector('[data-variant-id]');
    const variantsJson = form.querySelector('[data-variants-json]');
    const variants = variantsJson ? JSON.parse(variantsJson.textContent || '[]') : [];
    const priceDisplay = document.querySelector('[data-product-price]');
    const compareDisplay = document.querySelector('[data-product-compare-price]');
    const addToCartText = document.querySelector('[data-add-to-cart-text]');
    const addToCartButton = document.querySelector('[data-add-to-cart]');

    const formatMoney = (cents) => {
      const formatter = window.Shopify?.formatMoney || ((cents) => `₹${(cents / 100).toFixed(2)}`);
      return formatter(cents);
    };

    const updateVariant = () => {
      const selectedOptions = Array.from(options)
        .filter(opt => opt.checked)
        .map(opt => opt.value);

      const matchedVariant = variants.find(v => {
        return v.options.every((opt, i) => opt === selectedOptions[i]);
      });

      if (matchedVariant && select) {
        select.value = matchedVariant.id;
        select.dispatchEvent(new Event('change', { bubbles: true }));

        if (priceDisplay) priceDisplay.textContent = formatMoney(matchedVariant.price);
        if (compareDisplay) {
          if (matchedVariant.compare_at_price && matchedVariant.compare_at_price > matchedVariant.price) {
            compareDisplay.textContent = formatMoney(matchedVariant.compare_at_price);
            compareDisplay.classList.remove('hidden');
          } else {
            compareDisplay.classList.add('hidden');
          }
        }

        if (addToCartButton) {
          addToCartButton.disabled = !matchedVariant.available;
        }
        if (addToCartText) {
          addToCartText.textContent = matchedVariant.available ? 'Add to cart' : 'Sold out';
        }
      }
    };

    options.forEach(opt => opt.addEventListener('change', updateVariant));
  });
}

/**
 * Quantity Stepper logic
 */
function initQtySteppers() {
  // Handled via global delegation below
}

// Global click handler for quantity buttons
document.addEventListener('click', (e) => {
  const minus = e.target.closest('[data-qty-minus]');
  const plus = e.target.closest('[data-qty-plus]');

  if (minus || plus) {
    const container = e.target.closest('[data-qty-container]') || e.target.parentElement;
    const input = container.querySelector('[data-qty-input]');
    if (!input) return;

    // Don't handle if it's inside the cart drawer (handled by initCartDrawer delegation)
    if (e.target.closest('[data-cart-line]')) return;

    let val = parseInt(input.value) || 1;
    if (minus) val = Math.max(1, val - 1);
    if (plus) val += 1;
    input.value = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
});

/**
 * Accordion logic (product page)
 */
function initAccordions() {
  const triggers = document.querySelectorAll('[data-accordion-trigger]');

  const toggleAccordion = (trigger, forceOpen = null) => {
    const accordion = trigger.closest('[data-accordion]');
    if (!accordion) return;
    const content = accordion.querySelector('[data-accordion-content]');
    const icon = trigger.querySelector('svg');
    const isOpen = content.classList.contains('max-h-[900px]');
    const shouldOpen = forceOpen !== null ? forceOpen : !isOpen;

    // Close all siblings
    const parent = accordion.parentElement;
    if (parent) {
      parent.querySelectorAll('[data-accordion]').forEach(other => {
        if (other === accordion) return;
        const otherContent = other.querySelector('[data-accordion-content]');
        const otherIcon = other.querySelector('[data-accordion-trigger] svg');
        if (otherContent) {
          otherContent.classList.remove('max-h-[900px]', 'pb-6', 'opacity-100');
          otherContent.classList.add('max-h-0', 'opacity-0');
        }
        if (otherIcon) otherIcon.classList.remove('rotate-180');
      });
    }

    if (shouldOpen) {
      content.classList.remove('max-h-0', 'opacity-0');
      content.classList.add('max-h-[900px]', 'pb-6', 'opacity-100');
      if (icon) icon.classList.add('rotate-180');
    } else {
      content.classList.remove('max-h-[900px]', 'pb-6', 'opacity-100');
      content.classList.add('max-h-0', 'opacity-0');
      if (icon) icon.classList.remove('rotate-180');
    }
  };

  triggers.forEach(trigger => {
    trigger.addEventListener('click', () => toggleAccordion(trigger));
  });

  // Open first accordion by default
  if (triggers.length > 0) {
    toggleAccordion(triggers[0], true);
  }
}
